import { useMemo, useState } from 'react';

import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import UpcomingRoundedIcon from '@mui/icons-material/UpcomingRounded';
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { usePermissions } from '@/shared/permissions';
import { useMerchantPortalSettingsSectionVisibility } from '@/shared/navigation/merchantPortalNavVisibility';
import { PageContainer } from '@/shared/ui';

import { SETTINGS_GROUPS, settingsNavForGroup } from '../settingsNav';
import type { SettingsNavItem } from '../settingsNav';

/**
 * Store-settings overview: a permission-aware, **grouped** and searchable index
 * of every settings domain. Domains are organised into meaningful categories
 * (Store, Selling, Fulfillment, …) so the page is scannable instead of a flat
 * wall of tiles. Domains the employee cannot view are shown locked.
 */
export function SettingsIndexPage() {
  const { hasPermission } = usePermissions();
  const sectionVisibility = useMerchantPortalSettingsSectionVisibility();
  const [filter, setFilter] = useState('');
  const query = filter.trim().toLowerCase();

  const groups = useMemo(
    () =>
      SETTINGS_GROUPS.map((group) => ({
        group,
        items: settingsNavForGroup(group).filter(
          (nav) =>
            !nav.hiddenFromUi &&
            (!query ||
              nav.label.toLowerCase().includes(query) ||
              nav.description.toLowerCase().includes(query)),
        ),
      }))
        .filter((entry) => (entry.group.featureKey ? sectionVisibility[entry.group.featureKey] : true))
        .filter((entry) => entry.items.length > 0),
    [query, sectionVisibility],
  );

  return (
    <PageContainer
      title="Store settings"
      subtitle="Configure how your online store behaves. Pick a category to get started."
    >
      <TextField
        value={filter}
        onChange={(event) => setFilter(event.target.value)}
        placeholder="Search settings…"
        size="small"
        aria-label="Search settings"
        sx={{ mb: 4, maxWidth: { sm: 460 } }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />

      {groups.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4 }}>
          No settings match “{filter}”.
        </Typography>
      ) : (
        <Stack spacing={4}>
          {groups.map(({ group, items }) => (
            <Box key={group.key} component="section">
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Box
                  aria-hidden
                  sx={{
                    display: 'grid',
                    placeItems: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    flexShrink: 0,
                    color: '#fff',
                    background: (t) =>
                      `linear-gradient(135deg, ${t.palette.primary.light}, ${t.palette.primary.dark})`,
                  }}
                >
                  {group.icon}
                </Box>
                <Box>
                  <Typography variant="h6">{group.label}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {group.description}
                  </Typography>
                </Box>
              </Stack>

              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    xl: 'repeat(3, 1fr)',
                  },
                }}
              >
                {items.map((nav) => (
                  <SettingsDomainCard
                    key={nav.key}
                    nav={nav}
                    canView={hasPermission(nav.viewPermission)}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </PageContainer>
  );
}

interface SettingsDomainCardProps {
  nav: SettingsNavItem;
  canView: boolean;
}

function SettingsDomainCard({ nav, canView }: SettingsDomainCardProps) {
  const comingSoon = !canView && Boolean(nav.preview);
  const content = (
    <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ p: 2.5, height: '100%' }}>
      <Box
        aria-hidden
        sx={{
          display: 'grid',
          placeItems: 'center',
          width: 44,
          height: 44,
          borderRadius: 2.5,
          flexShrink: 0,
          color: canView ? '#fff' : 'text.disabled',
          background: (t) =>
            canView
              ? `linear-gradient(135deg, ${t.palette.primary.light}, ${t.palette.primary.dark})`
              : t.palette.action.hover,
        }}
      >
        {nav.icon}
      </Box>
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1" fontWeight={700}>
            {nav.label}
          </Typography>
          {comingSoon ? (
            <Chip
              size="small"
              variant="outlined"
              color="primary"
              icon={<UpcomingRoundedIcon sx={{ fontSize: 14 }} />}
              label="Coming soon"
            />
          ) : !canView ? (
            <Chip
              size="small"
              variant="outlined"
              icon={<LockOutlinedIcon sx={{ fontSize: 14 }} />}
              label="No access"
            />
          ) : nav.preview ? (
            <Chip
              size="small"
              variant="outlined"
              color="primary"
              icon={<UpcomingRoundedIcon sx={{ fontSize: 14 }} />}
              label="Preview"
            />
          ) : null}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {nav.description}
        </Typography>
      </Box>
      {canView || comingSoon ? (
        <ChevronRightRoundedIcon sx={{ color: 'text.disabled', alignSelf: 'center' }} />
      ) : null}
    </Stack>
  );

  return (
    <Card sx={{ opacity: canView ? 1 : 0.7 }}>
      {canView ? (
        <CardActionArea component={RouterLink} to={nav.path} sx={{ height: '100%', alignItems: 'stretch' }}>
          {content}
        </CardActionArea>
      ) : (
        content
      )}
    </Card>
  );
}
